import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { ChevronLeft, Printer, Users, CalendarCheck, Trophy, AlertTriangle, ShieldAlert } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import { playerAttendanceService, ATTENDANCE_AT_RISK_THRESHOLD } from '../../services/playerAttendanceService';
import { InjuryService } from '../../services/injuryService';
import type { TeamAttendanceSummary } from '../../types/playerCard';
import { AttendanceTrendChart } from './AttendanceTrendChart';

interface InjurySummary {
  healthy: number;
  questionable: number;
  doubtful: number;
  out: number;
  total: number;
}

type SortKey = 'name' | 'attendance';

export function TeamAttendanceReport() {
  const { teamId } = useParams<{ teamId: string }>();

  const [summary, setSummary] = useState<TeamAttendanceSummary | null>(null);
  const [availability, setAvailability] = useState<InjurySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('attendance');

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [summaryResult, availabilityResult] = await Promise.all([
          playerAttendanceService.getTeamAttendanceSummary(teamId),
          InjuryService.getTeamInjurySummary(teamId),
        ]);
        if (!cancelled) {
          setSummary(summaryResult);
          setAvailability(availabilityResult);
        }
      } catch (error) {
        console.error('[TeamAttendanceReport] Error loading attendance summary:', error);
        toast.error('Failed to load attendance report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const sortedPlayers = useMemo(() => {
    if (!summary) return [];
    const players = [...summary.players];
    if (sortKey === 'name') {
      players.sort((a, b) => a.last_name.localeCompare(b.last_name));
    } else {
      players.sort((a, b) => a.stats.attendance_percentage - b.stats.attendance_percentage);
    }
    return players;
  }, [summary, sortKey]);

  if (!teamId) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4" />
        <p className="caption">Loading attendance report...</p>
      </div>
    );
  }

  const hasEvents = !!summary && summary.total_practices + summary.total_games > 0;

  return (
    <div className="print:px-0">
      <PageHeader
        title="Attendance Report"
        icon={<Users className="h-6 w-6" />}
        actions={
          <>
            <Link
              to={`/team/${teamId}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-stone-600 hover:text-primary border border-stone-200 rounded-lg hover:border-primary transition-colors print:hidden"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors print:hidden"
              aria-label="Print attendance report"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
          </>
        }
      />

      {!hasEvents ? (
        <div className="text-center py-16 bg-white rounded-lg shadow-md">
          <CalendarCheck className="mx-auto h-12 w-12 text-stone-400 mb-4" />
          <p className="text-stone-700 font-medium">No practices or games logged yet</p>
          <p className="text-sm text-stone-500 mt-1">
            Attendance will show up here once you start logging practices and games.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Stat tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600 mb-1">Overall Attendance</div>
              <div className="text-2xl font-bold text-primary">{summary!.overall_attendance_percentage}%</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600 mb-1">Practices Logged</div>
              <div className="text-2xl font-bold text-gray-900">{summary!.total_practices}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600 mb-1">Games Logged</div>
              <div className="text-2xl font-bold text-gray-900">{summary!.total_games}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                At-Risk Players
              </div>
              <div className="text-2xl font-bold text-red-600">{summary!.at_risk_players.length}</div>
              <div className="text-xs text-gray-500 mt-1">Below {ATTENDANCE_AT_RISK_THRESHOLD}%</div>
            </div>
          </div>

          {/* Trend chart */}
          <AttendanceTrendChart trend={summary!.trend} />

          {/* Roster availability snapshot */}
          {availability && availability.total > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShieldAlert className="h-5 w-5" />
                Roster Availability
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-800">{availability.healthy}</div>
                  <div className="text-xs text-green-600">Healthy</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-800">{availability.questionable}</div>
                  <div className="text-xs text-yellow-600">Questionable</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-800">{availability.doubtful}</div>
                  <div className="text-xs text-orange-600">Doubtful</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-800">{availability.out}</div>
                  <div className="text-xs text-red-600">Out</div>
                </div>
              </div>
            </div>
          )}

          {/* Per-player table */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Player Attendance
              </h3>
              <div className="flex items-center gap-2 text-sm print:hidden">
                <span className="text-gray-500">Sort by:</span>
                <button
                  type="button"
                  onClick={() => setSortKey('attendance')}
                  className={`px-2 py-1 rounded ${sortKey === 'attendance' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  aria-label="Sort players by attendance percentage"
                  aria-pressed={sortKey === 'attendance'}
                >
                  Attendance %
                </button>
                <button
                  type="button"
                  onClick={() => setSortKey('name')}
                  className={`px-2 py-1 rounded ${sortKey === 'name' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  aria-label="Sort players by name"
                  aria-pressed={sortKey === 'name'}
                >
                  Name
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4 font-medium">Player</th>
                    <th className="py-2 pr-4 font-medium">Practices</th>
                    <th className="py-2 pr-4 font-medium">Games</th>
                    <th className="py-2 pr-4 font-medium">Attendance %</th>
                    <th className="py-2 font-medium">Last Attended</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPlayers.map((p) => {
                    const atRisk = p.stats.attendance_percentage < ATTENDANCE_AT_RISK_THRESHOLD;
                    return (
                      <tr key={p.player_id} className={`border-b border-gray-100 ${atRisk ? 'bg-red-50' : ''}`}>
                        <td className="py-2 pr-4 text-gray-900 font-medium">
                          {p.number != null && <span className="text-gray-400 mr-1">#{p.number}</span>}
                          {p.first_name} {p.last_name}
                          {atRisk && (
                            <AlertTriangle className="inline-block h-3.5 w-3.5 text-red-500 ml-1.5 -mt-0.5" aria-label="At risk on attendance" />
                          )}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">
                          {p.stats.practices_present}/{p.stats.total_practices}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">
                          {p.stats.games_present}/{p.stats.total_games}
                        </td>
                        <td className={`py-2 pr-4 font-semibold ${atRisk ? 'text-red-600' : 'text-gray-900'}`}>
                          {p.stats.attendance_percentage}%
                        </td>
                        <td className="py-2 text-gray-600">
                          {p.stats.last_attended ? format(new Date(p.stats.last_attended), 'MMM d, yyyy') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {sortedPlayers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-gray-500">
                        No players on the roster yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print\\:px-0, .print\\:px-0 * {
              visibility: visible;
            }
            .print\\:px-0 {
              position: absolute;
              left: 0;
              top: 0;
            }
            .print\\:hidden {
              display: none !important;
            }
          }
        `}
      </style>
    </div>
  );
}

export default TeamAttendanceReport;
